import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { ContextOperation } from "../../../../context/OperationContext";
import useOperationTranslation from "../../../../hooks/useOperationTranslation";
import useSnackbar from "../../../../hooks/useSnackbar";
import useToggle from "../../../../hooks/useToggle";
import { ContextDamageForm } from "../../DamageFormContext";
import GeneralInfoCard from "../../generalInfo/GeneralInfoCard";
import SectionCardActions from "../../SectionCardActions";
import SectionCardHeader from "../../SectionCardHeader";
import useDamageFormActions from "../../service/DamageFormActions";
import DamageFormService from "../../service/DamageFormService";
import { DamageFormTypes } from "../../Types";
import Dialogs from "../dialogs";
import TaskStatus, { ITaskStatus } from "../TaskStatus";
import EventDetails from "./EventDetails";
import VehicleStatus from "./VehicleStatus";
import { useHasScope } from "../../../../../../config/globalConfig";
import { GlobalContext } from "../../../../../../util/state/GlobalContextProvider";
import GeneratePurchaseOrderService from "../../service/GeneratePurchaseOrderService";
import { yupResolver } from "@hookform/resolvers/yup";
import { purchaseOrderSchema } from "../../service/GeneratePurchaseOrderSchema";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useRecoilValue } from "recoil";
import { generalInfoStepTimezoneState } from "../../creation/GeneralInfoStep";
import { convertUtcToTzIsoDayJs, revertUtcToTzIsoDayJs } from "../../../../../../util/timezoneConversion";

/** LPP-25823: codice applicativo restituito dal backend quando si tenta di svuotare targa/VIN con RA associato */
const PLATE_VIN_LOCKED_BY_RA = "PLATE_VIN_LOCKED_BY_RA";

/** Legge `errorCode` dal body dell'errore (oggetto JSON oppure stringa JSON, a seconda del passaggio dal BFF). */
const getErrorCode = (error: any): string | undefined => {
  const data = error?.response?.data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data)?.errorCode;
    } catch {
      return undefined;
    }
  }
  return data?.errorCode;
};

const Task1 = () => {
  const hasScope = useHasScope;

  const { tenant, setLoading } = useContext(ContextOperation);
  const { generalInfo, task1, task3, rsList } = useContext(ContextDamageForm);
  const { globalData } = useContext(GlobalContext);

  const t = useOperationTranslation();
  const { i18n } = useTranslation();

  const snackBar = useSnackbar();

  const timezone = useRecoilValue(generalInfoStepTimezoneState);

  const form = useForm<DamageFormTypes.ITask1Update>({
    defaultValues: {
      lmsTechnicalStop: convertUtcToTzIsoDayJs(task1?.lmsTechnicalStop, timezone)?.toISOString(),
      lmsTechnicalStopEnd: convertUtcToTzIsoDayJs(task1?.lmsTechnicalStopEnd, timezone)?.toISOString(),
      repairDeliveryDate: convertUtcToTzIsoDayJs(task1?.repairDeliveryDate, timezone)?.toISOString(),
      deliveryDate: convertUtcToTzIsoDayJs(task1?.deliveryDate, timezone)?.toISOString(),
      quoteReceivalDate: convertUtcToTzIsoDayJs(task1?.quoteReceivalDate, timezone)?.toISOString(),
      quoteNegationDate: convertUtcToTzIsoDayJs(task1?.quoteNegationDate, timezone)?.toISOString(),
      quoteAuthorizationDate: convertUtcToTzIsoDayJs(task1?.quoteAuthorizationDate, timezone)?.toISOString(),
      replacementOrderDate: convertUtcToTzIsoDayJs(task1?.replacementOrderDate, timezone)?.toISOString(),
      replacementUnavailabilityDate: convertUtcToTzIsoDayJs(task1?.replacementUnavailabilityDate, timezone)?.toISOString(),
      replacementArrivalDate: convertUtcToTzIsoDayJs(task1?.replacementArrivalDate, timezone)?.toISOString(),
      repairStartDate: convertUtcToTzIsoDayJs(task1?.repairStartDate, timezone)?.toISOString(),
      repairEndDate: convertUtcToTzIsoDayJs(task1?.repairEndDate, timezone)?.toISOString(),
      repairEstimateDate: convertUtcToTzIsoDayJs(task1?.repairEstimateDate, timezone)?.toISOString(),
      plateLossComplaintDate: convertUtcToTzIsoDayJs(task1?.plateLossComplaintDate, timezone)?.toISOString(),
      plateSubstitutionDate: convertUtcToTzIsoDayJs(task1?.plateSubstitutionDate, timezone)?.toISOString(),
      dismantlingDate: convertUtcToTzIsoDayJs(task1?.dismantlingDate, timezone)?.toISOString(),
      sequestrationStartDate: convertUtcToTzIsoDayJs(task1?.sequestrationStartDate, timezone)?.toISOString(),
      sequestrationEndDate: convertUtcToTzIsoDayJs(task1?.sequestrationEndDate, timezone)?.toISOString(),

      raDate: convertUtcToTzIsoDayJs(task1?.raDate, timezone)?.toISOString(),
      expectedRentalEnd: convertUtcToTzIsoDayJs(task1?.expectedRentalEnd, timezone)?.toISOString(),
      actualRentalEnd: convertUtcToTzIsoDayJs(task1?.actualRentalEnd, timezone)?.toISOString(),

      // repairRecoveryDate:,
      // departureLmsId:,
    }
  });
  const purchaseOrderForm = useForm<DamageFormTypes.PurchaseOrderFormTypes>({ resolver: yupResolver(purchaseOrderSchema) });

  const [openCloseConfirm, toggleCloseConfirm] = useToggle();

  const [taskStatus, setTaskStatus] = useState<ITaskStatus>({});
  const [loadComponent, setLoadComponent] = useState(0);

  const [reopenCloseConfirm, toggleReopenConfirm] = useToggle();

  const actions = useDamageFormActions();

  const { id } = useParams();

  const basePath = useMemo(() => `/operation/damages/forms/details/${id}`, []);
  const navigate = useNavigate();

  useEffect(() => {
    if (!task1) {
      actions.getTask1();
    }
    if (!task3) {
      actions.getTask3();
    }

    setLoadComponent(1);
  }, []);

  const onFormReset = () => {
    form.reset({
      damageFormId: generalInfo?.id,
      legalLetter: task1?.legalLetter,
      repairRecoveryDate: task1?.repairRecoveryDate,
      repairShopSiren: task1?.repairShopSiren,
      repairShopAddress: task1?.repairShopAddress,
      repairShopCity: task1?.repairShopCity,
      repairShopMail: task1?.repairShopMail,
      repairShopName: task1?.repairShopName,
      repairShopNotes: task1?.repairShopNotes,
      repairShopPhone: task1?.repairShopPhone,
      repairShopRepresentative: task1?.repairShopRepresentative,
      rescuedBy: task1?.rescuedBy,
      sequestration: task1?.sequestration,
      seriousDamage: task1?.seriousDamage,
      formManagementLmsId: task1?.formManagementLms.id,
      repairShopSapId: rsList?.find(
        (shop) => shop.label === task1?.repairShopName
      )?.sapId,
      raMail: task1?.raMail,
      raId: task1?.raId,
      raOwner: task1?.raOwner,
      raNumber: task1?.raNumber,
      raSectional: task1?.raSectional,
      raDate: convertUtcToTzIsoDayJs(task1?.raDate, timezone)?.toISOString(),
      renewalsSubstitutions: task1?.renewalsSubstitutions,
      vehicle: task1?.vehicle,
      plate: task1?.plate,
      vin: task1?.vin,
      vehicleGroup: task1?.vehicleGroup,
      vehicleOwner: task1?.vehicleOwner,
      arrivalLmsId: task1?.arrivalLms.id,
      departureLmsId: task1?.departureLms.id,
      expectedRentalEnd: convertUtcToTzIsoDayJs(task1?.expectedRentalEnd, timezone)?.toISOString(),
      actualRentalEnd: convertUtcToTzIsoDayJs(task1?.actualRentalEnd, timezone)?.toISOString(),
      rentalKm: task1?.rentalKm,
      inspectionKm: task1?.inspectionKm,
      rentalFuelLevel: task1?.rentalFuelLevel,
      inspectionFuelLevel: task1?.inspectionFuelLevel,
      rentalBatteryLevel: task1?.rentalBatteryLevel,
      inspectionBatteryLevel: task1?.inspectionBatteryLevel,
      theftFormCode: task1?.theftFormCode,
      misappropriationFormCode: task1?.misappropriationFormCode,
      customerSapId: task1?.customerSapId,
      lmsTechnicalStop: convertUtcToTzIsoDayJs(task1?.lmsTechnicalStop, timezone)?.toISOString(),
      lmsTechnicalStopEnd: convertUtcToTzIsoDayJs(task1?.lmsTechnicalStopEnd, timezone)?.toISOString(),
      repairDeliveryDate: convertUtcToTzIsoDayJs(task1?.repairDeliveryDate, timezone)?.toISOString(),
      deliveryDate: convertUtcToTzIsoDayJs(task1?.deliveryDate, timezone)?.toISOString(),
      quoteReceivalDate: convertUtcToTzIsoDayJs(task1?.quoteReceivalDate, timezone)?.toISOString(),
      quoteNegationDate: convertUtcToTzIsoDayJs(task1?.quoteNegationDate, timezone)?.toISOString(),
      quoteAuthorizationDate: convertUtcToTzIsoDayJs(task1?.quoteAuthorizationDate, timezone)?.toISOString(),
      replacementOrderDate: convertUtcToTzIsoDayJs(task1?.replacementOrderDate, timezone)?.toISOString(),
      replacementUnavailabilityDate: convertUtcToTzIsoDayJs(task1?.replacementUnavailabilityDate, timezone)?.toISOString(),
      replacementArrivalDate: convertUtcToTzIsoDayJs(task1?.replacementArrivalDate, timezone)?.toISOString(),
      repairStartDate: convertUtcToTzIsoDayJs(task1?.repairStartDate, timezone)?.toISOString(),
      repairEndDate: convertUtcToTzIsoDayJs(task1?.repairEndDate, timezone)?.toISOString(),
      repairEstimateDate: convertUtcToTzIsoDayJs(task1?.repairEstimateDate, timezone)?.toISOString(),
      plateLossComplaintDate: convertUtcToTzIsoDayJs(task1?.plateLossComplaintDate, timezone)?.toISOString(),
      plateSubstitutionDate: convertUtcToTzIsoDayJs(task1?.plateSubstitutionDate, timezone)?.toISOString(),
      dismantlingDate: convertUtcToTzIsoDayJs(task1?.dismantlingDate, timezone)?.toISOString(),
      sequestrationStartDate: convertUtcToTzIsoDayJs(task1?.sequestrationStartDate, timezone)?.toISOString(),
      sequestrationEndDate: convertUtcToTzIsoDayJs(task1?.sequestrationEndDate, timezone)?.toISOString(),
    });
  };

  useEffect(() => {
    setTaskStatus({
      open: task1?.open,
      openingDate: task1?.createDate,
      openingUser: task1?.createUsername,
      closeDate: task1?.closeDate,
      closeUser: task1?.closeUsername,
    });

    onFormReset();
  }, [task1]);

  const getUpdate = (data: DamageFormTypes.ITask1Update) => {
    setLoading(true);
    DamageFormService.details.task1Update.put.updateTask1(data).then(
      () => {
        actions.getTask1();
        actions.getTask2();
        actions.getGeneralInfo();
        snackBar.show(
          "success",
          t("damageForm.details.task1.snackbar.update.success")
        );
        setLoading(false);
      },
      (error) => {

        if (getErrorCode(error) === PLATE_VIN_LOCKED_BY_RA) {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.errorPlateVinLocked")
          );
        }
        else if (error.response.status == 422) {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.error422")
          );
        }
        else if (error.response.status == 409) {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.error409")
          );
        }
        else if (error.response.status == 400) {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.error400")
          );
        }
        else if (error.response.status == 404) {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.error404")
          );
        } else if (error.response.status == 406) {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.error406")
          );
        } else {
          snackBar.show(
            "error",
            t("damageForm.details.task1.snackbar.update.error")
          );
        }
        setLoading(false);
      }
    );
  };

  const onCloseTask1 = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.task1.put.closeById(id).then(
      () => {
        toggleCloseConfirm();
        actions.getTask1();
        actions.getGeneralInfo();
        snackBar.show(
          "success",
          t("damageForm.details.task1.snackbar.close.success")
        );
        setLoading(false);
      },
      () => {
        toggleCloseConfirm();
        snackBar.show(
          "error",
          t("damageForm.details.task1.snackbar.close.error")
        );
        setLoading(false);
      }
    );
  };

  const onReopen = () => {
    if (!id) return;

    setLoading(true);
    DamageFormService.details.task1.put.reopenByid(tenant, id).then(
      () => {
        toggleReopenConfirm();
        actions.getTask1();
        actions.getGeneralInfo();
        snackBar.show(
          "success",
          t("damageForm.details.task1.snackbar.reopen.success")
        );
        setLoading(false);
      },
      () => {
        snackBar.show(
          "error",
          t("damageForm.details.task1.snackbar.reopen.error")
        );

        toggleReopenConfirm();
        setLoading(false);
      }
    );
  };

  const checkAuthDMSOperator = () => {
    if (task1?.formManagementLms != null && task1?.formManagementLms?.id != null) {

      return (hasScope("SCOPE_UPDATE_DAMAGE_TASK_ONE_MANAGEMENT_DMS")
        && globalData?.user?.mobilityStores.filter(
          ({ sapId }: any) => sapId === task1?.formManagementLms?.id
        ).length > 0);

    } else {
      return hasScope("SCOPE_UPDATE_DAMAGE_TASK_ONE_MANAGEMENT_DMS");
    }
  }

  function getIvaFromGross(gross: number, ivaPercentage: number) {
    return Number((gross - gross / (1 + ivaPercentage / 100)).toFixed(2));
  }

  function getNetFromGross(gross: number, ivaPercentage: number) {
    return Number((gross / (1 + ivaPercentage / 100)).toFixed(2));
  }

  function getDealers() {
    var ris: string = "";

    console.log(globalData.user?.dealers)
    console.log(globalData.user?.dealers?.name)

    globalData.user?.dealers.forEach((dealer: any) => {
      ris += dealer?.name;
      ris += ", ";
    });

    ris = ris.slice(0, ris.length - 2);

    return ris;
  }

  function fillFormGeneratePurchaseOrder() {
    const totalGrossPrice = Number(String(task3?.gridAmount).replace(',', '.'));
    const ivaPercentage = 20;
    const dealers = getDealers();
    const language = i18n.language;

    const purchaseElements: any = [{
      description: generalInfo?.event?.descr,
      quantity: 1, // Always 1
      unitNetPrice: getNetFromGross(totalGrossPrice, ivaPercentage) / 1, // totalNetPrice / quantity
      totalNetPrice: getNetFromGross(totalGrossPrice, ivaPercentage), // e' senza iva,
      ivaPrice: getIvaFromGross(totalGrossPrice, ivaPercentage), // 20% di 390.18
      totalPrice: totalGrossPrice,
    }]

    purchaseOrderForm.reset({
      /*
      repairShopSapId: rsList?.find(
        (shop) => shop.label === task1?.repairShopName
      )?.sapId,
      repairShopSiren: task1?.repairShopSiren,
      repairShopName: task1?.repairShopName,
      repairShopAddress: task1?.repairShopAddress,
      repairShopCity: task1?.repairShopCity,
      repairShopPhone: task1?.repairShopPhone,
      repairShopMail: task1?.repairShopMail,
      plate: task1?.plate,
      vin: task1?.vin,
      damageDetail: undefined ?? "a", // TOD?
      gridAmount: undefined // TOD?
      */

      damageFormId: generalInfo?.id,
      dealer: dealers,
      tenant: generalInfo?.tenant,
      language: language,
      purchaseOrderDate: dayjs(task1?.createDate).format("DD/MM/YYYY HH:mm"),
      ivaPercentage: ivaPercentage,
      ivaPrice: getIvaFromGross(totalGrossPrice, ivaPercentage), // 20% di 390.18
      ivaTotalPrice: getIvaFromGross(totalGrossPrice, ivaPercentage),
      totalGrossPrice: totalGrossPrice,
      totalNetPrice: getNetFromGross(totalGrossPrice, ivaPercentage), // e' senza iva
      totalPrice: totalGrossPrice,
      repairShopAddress: task1?.repairShopAddress,
      repairShopBillingAddress: task1?.repairShopAddress,
      purchaseElements: purchaseElements,

      // -----Valori non utilizzati nel pdf -----
      repairShopAddressCity: task1?.repairShopCity,
      repairShopSiren: task1?.repairShopSiren, //TODO add: ?? task1.vatNumber,
      repairShopName: task1?.repairShopName,
      repairShopPhone: task1?.repairShopPhone,
      repairShopMail: task1?.repairShopMail,

      vehicleVin: task1?.vin,
      vehiclePlate: task1?.plate,
    });
  }

  function generatePurchaseOrder() {
    fillFormGeneratePurchaseOrder();
    setLoading(true);

    purchaseOrderForm.handleSubmit(
      (damageData) => {
        GeneratePurchaseOrderService(damageData).then(
          (response) => {
            snackBar.show(
              "success",
              t("damageForm.details.task1.snackbar.generatePurchaseOrder.success" as any)
            );
            setLoading(false);

            // Go to messages page
            navigate(`${basePath}/messages?tenant=${tenant}&language=fr&msg=DAMAGE_FORM_PDF`);
            window.location.reload();
          },
          (error) => {
            snackBar.show(
              "error",
              t("damageForm.details.task1.snackbar.generatePurchaseOrder.error" as any)
            );
            setLoading(false);
          }
        );
      },
      (formErrors) => {
        const errorMessage = Object.values(formErrors)
          .map((error) => error?.message)
          .filter(Boolean)
          .join("\n");

        snackBar.show("error", errorMessage);
        setLoading(false);
      }
    )();
  }

  return (
    <Mui.Box mt={2}>
      <form onSubmit={form.handleSubmit((data) => {
        data.lmsTechnicalStop = revertUtcToTzIsoDayJs(data?.lmsTechnicalStop, timezone)?.toISOString()!;
        data.lmsTechnicalStopEnd = revertUtcToTzIsoDayJs(data?.lmsTechnicalStopEnd, timezone)?.toISOString()!;
        data.repairDeliveryDate = revertUtcToTzIsoDayJs(data?.repairDeliveryDate, timezone)?.toISOString()!;
        data.deliveryDate = revertUtcToTzIsoDayJs(data?.deliveryDate, timezone)?.toISOString()!;
        data.quoteReceivalDate = revertUtcToTzIsoDayJs(data?.quoteReceivalDate, timezone)?.toISOString()!;
        data.quoteNegationDate = revertUtcToTzIsoDayJs(data?.quoteNegationDate, timezone)?.toISOString()!;
        data.quoteAuthorizationDate = revertUtcToTzIsoDayJs(data?.quoteAuthorizationDate, timezone)?.toISOString()!;
        data.replacementOrderDate = revertUtcToTzIsoDayJs(data?.replacementOrderDate, timezone)?.toISOString()!;
        data.replacementUnavailabilityDate = revertUtcToTzIsoDayJs(data?.replacementUnavailabilityDate, timezone)?.toISOString()!;
        data.replacementArrivalDate = revertUtcToTzIsoDayJs(data?.replacementArrivalDate, timezone)?.toISOString()!;
        data.repairStartDate = revertUtcToTzIsoDayJs(data?.repairStartDate, timezone)?.toISOString()!;
        data.repairEndDate = revertUtcToTzIsoDayJs(data?.repairEndDate, timezone)?.toISOString()!;
        data.repairEstimateDate = revertUtcToTzIsoDayJs(data?.repairEstimateDate, timezone)?.toISOString()!;
        data.plateLossComplaintDate = revertUtcToTzIsoDayJs(data?.plateLossComplaintDate, timezone)?.toISOString()!;
        data.plateSubstitutionDate = revertUtcToTzIsoDayJs(data?.plateSubstitutionDate, timezone)?.toISOString()!;
        data.dismantlingDate = revertUtcToTzIsoDayJs(data?.dismantlingDate, timezone)?.toISOString()!;
        data.sequestrationStartDate = revertUtcToTzIsoDayJs(data?.sequestrationStartDate, timezone)?.toISOString()!;
        data.sequestrationEndDate = revertUtcToTzIsoDayJs(data?.sequestrationEndDate, timezone)?.toISOString()!;

        return getUpdate(data)
      })}>
        <Mui.Card>
          <SectionCardHeader>
            <Mui.Grid container spacing={2} mr={1}>
              {!task1?.open &&
                generalInfo?.tenant === "drivalia_fr" && (
                  <Mui.Grid item xs="auto">
                    <Mui.Button
                      variant="contained"
                      size="small"
                      onClick={generatePurchaseOrder}
                    >
                      <Mui.Typography color="inherit" variant="h6">
                        {t("damageForm.details.task1.header.btnGenerate")}
                      </Mui.Typography>
                    </Mui.Button>
                  </Mui.Grid>
                )}
              {task1?.open &&
                !generalInfo?.migrated &&
                hasScope("SCOPE_CLOSE_DAMAGE_DAMAGE_FORM") && (
                  <Mui.Grid item xs="auto">
                    <Mui.Button
                      variant="contained"
                      size="small"
                      startIcon={<MuiIcons.HighlightOffRounded />}
                      onClick={toggleCloseConfirm}
                    >
                      <Mui.Typography color="inherit" variant="h6">
                        {t("damageForm.details.task1.header.btnClose")}
                      </Mui.Typography>
                    </Mui.Button>
                  </Mui.Grid>
                )}
              {!task1?.open &&
                !generalInfo?.migrated &&
                hasScope("SCOPE_REOPEN_DAMAGE_TASK_ONE") && (
                  <Mui.Grid item xs="auto">
                    <Mui.Button
                      variant="contained"
                      size="small"
                      startIcon={<MuiIcons.HighlightOffRounded />}
                      onClick={toggleReopenConfirm}
                    >
                      <Mui.Typography color="inherit" variant="h6">
                        {t("damageForm.details.task1.header.btnReopen")}
                      </Mui.Typography>
                    </Mui.Button>
                  </Mui.Grid>
                )}
            </Mui.Grid>
          </SectionCardHeader>

          <Mui.CardContent>
            <Mui.Stack spacing={2}>
              <GeneralInfoCard />

              {loadComponent >= 1 ? (
                <TaskStatus
                  formInfo={taskStatus}
                  onComponentDidMount={setLoadComponent}
                />
              ) : (
                <Mui.Skeleton variant="rectangular" width="100%">
                  <TaskStatus />
                </Mui.Skeleton>
              )}

              {loadComponent >= 2 ? (
                <VehicleStatus
                  task1Fields={task1}
                  onComponentDidMount={setLoadComponent}
                  formInstanceUpdate={form}
                />
              ) : (
                <Mui.Skeleton variant="rectangular">
                  <VehicleStatus />
                </Mui.Skeleton>
              )}

              {loadComponent >= 3 ? (
                <EventDetails task1Fields={task1} formInstanceUpdate={form} />
              ) : (
                <Mui.Skeleton variant="rectangular">
                  <EventDetails />
                </Mui.Skeleton>
              )}
            </Mui.Stack>
          </Mui.CardContent>

          {(hasScope("SCOPE_UPDATE_DAMAGE_TASK_ONE") || checkAuthDMSOperator()) && (
            <SectionCardActions onClickCancel={onFormReset} />
          )}
        </Mui.Card>
      </form>

      <Dialogs.CloseTaskConfirmation
        open={openCloseConfirm}
        onClose={toggleCloseConfirm}
        taskName={t("damageForm.details.tabs.task1")}
        onCloseTask={onCloseTask1}
      />

      <Dialogs.ReopenTaskConfirmation
        open={reopenCloseConfirm}
        onReopen={toggleReopenConfirm}
        onReopenTask={onReopen}
        taskName={t("damageForm.details.tabs.task1")}
      />
    </Mui.Box >
  );
};
export default Task1;
